<template>
    <div class="ship-container">
        <div class="ship-card-pure" :class="`accent-${cardColorClass}`">

            <div class="ship-header">
                <div class="icon-wrapper">
                    <img :src="containerShipImage" alt="Icône Navire" class="ship-icon-pure">
                </div>
                <h2 class="ship-title">{{ shipName }}</h2>
            </div>

            <div class="ship-total-section">
                <p class="total-label">VALEUR TOTALE D'EXPÉDITION:</p>
                <div class="total-amount-wrapper">
                    <span class="total-currency">$</span>
                    <span class="total-value">{{ formattedTotal.replace('USD', '').trim() }}</span>
                </div>
            </div>

            <div class="bl-list-header">
                <span class="bl-count">{{ blList.length }} BLs Associés</span>
                <button class="btn-track-all" @click="trackFullShipment">
                    Suivre le Navire
                </button>
            </div>

            <div class="bl-list-wrapper">
                <div v-for="bl in blList" :key="bl" class="bl-item-pure">
                    <span class="bl-number-pure">{{ bl }}</span>
                    <button
                        class="btn-track-bl"
                        @click="trackShipment(bl)"
                    >
                        Suivre
                    </button>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: 'ShipCard',
    props: {
        shipName: {
            type: String,
            required: true
        },
        blList: {
            type: Array,
            required: true
        },
        totalAmount: {
            type: Number,
            required: true
        },
        // Nouvelle prop pour la couleur spécifique de la carte (ex: 'blue', 'green', 'orange')
        cardColorClass: {
            type: String,
            default: 'blue' // Couleur par défaut si non spécifiée
        }
    },
    data() {
        return {
            // Image générique de navire de conteneurs
            containerShipImage: 'https://cdn-icons-png.flaticon.com/512/3229/3229712.png' // Exemple d'icône
        }
    },
    computed: {
        formattedTotal() {
            return this.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        }
    },
    methods: {
        trackShipment(bl) {
            /**
             * Émet un événement 'track-bl' vers le composant parent,
             * en passant le navire et le BL sélectionné.
             */
            this.$emit('track-bl', {
                ship: this.shipName,
                bl: bl
            });
            console.log(`Suivi demandé pour le navire: ${this.shipName}, BL: ${bl}`);
        },
        trackFullShipment() {
            // Optionnel: Émettre un événement pour suivre tous les BLs d'un navire
            this.$emit('track-ship', {
                ship: this.shipName,
                bls: this.blList
            });
            console.log(`Suivi de tous les BLs pour le navire: ${this.shipName}`);
        }
    }
}
</script>

<style scoped>
/* Définition des Variables de Couleur */
:root {
    --color-primary: #004c99; /* Bleu Marine Profond (pour les accents) */
    --color-text-dark: #333333;
    --color-text-medium: #666666;
    --color-background-light: #f5f7f9;
    --color-total-success: #00b894; /* Vert pour les montants */

    /* Couleurs d'Accentuation Dynamiques (Accent Bars) */
    --accent-blue: #0077b6;
    --accent-green: #00b894;
    --accent-orange: #ff9f1c;
    --accent-red: #e76f51;
}

.ship-container {
    padding: 15px;
    width: 33.33%;
    box-sizing: border-box;
}

/* Carte principale (Remplaçant 'card') */
.ship-card-pure {
    background-color: #ffffff;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    overflow: hidden;
    position: relative;
    padding: 0;
}

.ship-card-pure:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
}

/* Accent de couleur vertical (remplace le header coloré) */
.ship-card-pure:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    background-color: var(--color-primary); /* Couleur par défaut */
}

/* Adaptation des accents de couleur */
.accent-blue:before { background-color: var(--accent-blue); }
.accent-green:before { background-color: var(--accent-green); }
.accent-orange:before { background-color: var(--accent-orange); }
.accent-red:before { background-color: var(--accent-red); }

/* ------------------ EN-TÊTE ------------------ */
.ship-header {
    display: flex;
    align-items: center;
    padding: 20px 20px 10px 20px;
    border-bottom: 1px solid #eeeeee;
}

.icon-wrapper {
    background-color: var(--color-background-light);
    border-radius: 50%;
    padding: 10px;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ship-icon-pure {
    width: 30px;
    height: 30px;
    filter: none;
}

.ship-title {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--color-text-dark);
    margin: 0;
}

/* ------------------ TOTAL ------------------ */
.ship-total-section {
    padding: 15px 20px;
    text-align: right;
    background-color: var(--color-background-light);
    margin-top: 10px;
    border-top: 1px solid #e0e0e0;
}

.total-label {
    font-size: 0.75rem;
    color: var(--color-text-medium);
    margin-bottom: 2px;
    font-weight: 500;
    letter-spacing: 0.5px;
}

.total-amount-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    line-height: 1;
}

.total-currency {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--color-total-success);
    margin-right: 4px;
}

.total-value {
    font-size: 2.2rem;
    font-weight: 800;
    color: var(--color-total-success);
}

/* ------------------ LISTE BL ------------------ */
.bl-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px 10px 20px;
}

.bl-count {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-text-dark);
}

.bl-list-wrapper {
    max-height: 150px;
    overflow-y: auto;
    padding: 0 20px 20px 20px;
}

.bl-item-pure {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px dashed #e5e5e5;
}

.bl-item-pure:last-child {
    border-bottom: none;
}

.bl-number-pure {
    font-size: 0.95rem;
    color: var(--color-text-medium);
    font-family: monospace; /* Utiliser une police à chasse fixe pour les numéros de suivi */
    font-weight: 500;
}

/* ------------------ BOUTONS ------------------ */

.btn-track-all, .btn-track-bl {
    padding: 5px 12px;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
    text-transform: uppercase;
}

/* Bouton Global */
.btn-track-all {
    background-color: var(--color-primary);
    color: #ffffff;
    border: none;
    font-weight: 600;
}

.btn-track-all:hover {
    background-color: #003e7e;
}

/* Bouton Individuel */
.btn-track-bl {
    background-color: transparent;
    color: var(--color-primary);
    border: 1px solid var(--color-primary);
    font-weight: 500;
}

.btn-track-bl:hover {
    background-color: var(--color-primary);
    color: #ffffff;
}

/* Scrollbar Stylisée pour le BL list */
.bl-list-wrapper::-webkit-scrollbar {
    width: 6px;
}
.bl-list-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
}
.bl-list-wrapper::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 10px;
}
.bl-list-wrapper::-webkit-scrollbar-thumb:hover {
    background: #003e7e;
}
</style>

